import { effect, inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, filter } from 'rxjs';
import { AuthService } from '@authentication';
import { PrivateMessageService } from '../core/private-messages/private-message.service';
import { OrderFormNavigatorService } from '../events/order-form/order-form-navigator.service';
import { PrivateMessageEventConsumerService } from '../events/private-message/private-message-event.consumer.service';
import { WalletStore } from '../modules/wallet/data-access/wallet.store';
import { UserSettingEventProducerService } from '../events/user-setting/user-setting-event-producer.service';
import { AppAutoUpdateService } from '../services/app-auto-update.service';
import { GeneralSettingsService } from '../services/general-settings.service';
import { PrivateMessageNotificationService } from '../services/private-message-notification.service';
import { SessionService } from '../services/session.service';
import { ScreenWakeService } from '../services/screen-wake.service';
import { ThemeService } from '../services/theme.service';
import { TradingTimeService } from '../services/trading-time.service';

export const EVENT_DRIVEN_STARTUP = new InjectionToken<void>('EVENT_DRIVEN_STARTUP');

/** How long a private-stream announcement waits before `/wallet/summary` is read again. */
export const WALLET_RELOAD_DELAY_MS = 2_000;

/**
 * Instantiates app-scoped event producers at kala-mobile startup
 * so preferences (and future producers) are available immediately.
 *
 * The producer publishes its cached settings on construction; this also pulls
 * the stored ones from the API as soon as there is a token to call it with, so
 * a session that starts on a new device does not run on stale defaults until
 * the settings sheet happens to be opened. The request waits for
 * authentication — firing it earlier would only earn a 401 and fall back to
 * the cache.
 *
 * `GeneralSettingsService` and `ScreenWakeService` are here for the same reason as
 * `ThemeService`: تنظیمات عمومی settings such as the bottom spacing and the screen
 * wake lock act on the document and the device rather than on a screen, so they
 * have to follow the stored setting for as long as the app is open — not only
 * while the settings page happens to be mounted.
 *
 * `PrivateMessageService` is instantiated here for the same reason: it opens the
 * private SSE stream off the authentication state itself, so it has to exist
 * from boot rather than wait for a component to inject it. Its consumers —
 * `PrivateMessageNotificationService` here, `OrdersComponent` when it is on
 * screen — are separate; the stream neither knows nor needs to know who is
 * listening.
 *
 * `SessionService` is here because the login it answers — the return from the
 * account server — happens before any component exists to inject it. Missing it
 * would mean the device is never announced for that session.
 *
 * `AppAutoUpdateService` is here so a newly deployed build replaces the cached one
 * whichever screen the trader is on.
 *
 * The wallet balance is read here for a related reason: قدرت خرید is printed by the
 * footer's bar on every main screen and by the order form's summary, neither of
 * which owns the figure. Loading it once at startup means the bar is never blank on
 * first paint and the order form never has to fetch a balance of its own.
 *
 * That first read is a starting point, not the session's figure. The balance moves
 * whenever the customer's money does — an order accepted, executed or cancelled,
 * money paid in — and none of those happen in this app, so the server announces
 * them: `getremain` on the private stream means «موجودی کهنه شده، دوباره بخوان»,
 * and is also raised on every reconnect, so a balance that changed while the stream
 * was down is picked up as soon as it is back. Subscribed here rather than in the
 * bar or the card because the store is one and the screens that read it are three.
 */
export function provideEventDrivenStartup() {
	return [
		{
			provide: EVENT_DRIVEN_STARTUP,
			useFactory: () => {
				const userSetting = inject(UserSettingEventProducerService);
				const authService = inject(AuthService);
				const walletStore = inject(WalletStore);
				const tradingTimeService = inject(TradingTimeService);
				const privateMessages = inject(PrivateMessageEventConsumerService);
				inject(OrderFormNavigatorService);
				inject(ThemeService);
				inject(GeneralSettingsService);
				inject(ScreenWakeService);
				inject(PrivateMessageService);
				inject(PrivateMessageNotificationService);
				inject(SessionService);
				inject(AppAutoUpdateService);

				let hydrated = false;
				effect(() => {
					if (authService.waitingForLogin() || hydrated) {
						return;
					}

					hydrated = true;
					// Fire-and-forget: `refresh` swallows API failures and keeps the cached settings.
					userSetting.refresh().catch(() => undefined);
					// Same bargain: the store reports its own failure, and the screens that
					// print the balance keep showing the last one they had.
					walletStore.load();
					tradingTimeService.load();
				});

				// Every later read of the wallet. Fire-and-forget for the same reason as the
				// first one, and `load` drops the answer to a superseded request, so a burst
				// of announcements settles on the last one rather than on whichever landed last.
				privateMessages.remainReloadRequested$
					.pipe(delay(WALLET_RELOAD_DELAY_MS), takeUntilDestroyed())
					.subscribe(() => {
						walletStore.load();
					});

				// A transfer landing is announced as the account's own text rather than as a
				// remain reload, and the money it moved has changed the balance it reports.
				privateMessages.textReceived$
					.pipe(
						filter(message => message.kind === 'Text_IME'),
						delay(WALLET_RELOAD_DELAY_MS),
						takeUntilDestroyed()
					)
					.subscribe(() => {
						walletStore.load();
					});
			},
			multi: true
		}
	];
}
